import { 
    Box,
    Button,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material";
import { CampaignInfoCell, CampaignEditCell} from "../CampaignCell";
import { statratRed } from "../../../../config/constants";
import { formatNumber, formatCurrency, formatDateTime } from "../../../../hooks/formatNumber";
import { Campaign } from "../../../../types/campaign";

const rowsPerPage = 5;

type RowsType = {
    data: Campaign[];
    adStatus: string;
    page: number;
    showAlert: (a: string, b: string, c: number) => void;
    fetchData: () => void;
}

const Rows = ({ data, adStatus, page, showAlert, fetchData }: RowsType) => {
    const emptyMessage = adStatus === "pendingApproval" ? "No campaigns awaiting approval" : `No ${adStatus} campaigns`;
    const submitMessage = "Are you sure you want to submit this ad? You will not be able to edit it until it has been reviewed.";
    const deleteMessage = `Are you sure you want to ${adStatus === "active" ? "stop" : "archive"} this ad? This cannot be undone.`;
    const expiredMessage = "This ad has passed it's end date and is no longer active. Would you like to archive it?";
    const cancelMessage = "Are you sure you want to cancel the review request? The ad will return to your drafts.";

    const pageData = data.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    return (
        <TableBody>
            {data.length === 0 ? (
                <TableRow key={0} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                        <Typography>{emptyMessage}</Typography>
                    </TableCell>
                </TableRow>
            ) : adStatus === "active" || adStatus === "past" ? (
                pageData.map((row) =>
                    <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <CampaignInfoCell ad={row} />
                        <TableCell align="right">
                            <Typography>{formatNumber(row.impressions ?? 0)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                            <Typography>{formatNumber(row.qualityImpressions ?? 0)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                            <Typography>{formatNumber(row.clicks ?? 0)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                            <Typography>{formatNumber(row.conversions ?? 0)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                            <Typography>{formatCurrency(row.spend ?? 0)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                            <Typography>{formatDateTime(row.startTime)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                            <Typography>{formatDateTime(row.endTime)}</Typography>
                        </TableCell>
                        {adStatus === "active" &&
                            <TableCell align="right" width={100}>
                                <Box display="flex" justifyContent="flex-end">
                                    {row.endTime * 1000 <= Date.now() ? (
                                        <Button
                                            variant="outlined"
                                            onClick={() => showAlert(expiredMessage, "disable", row.id)}
                                        >
                                            Archive
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            onClick={() => showAlert(deleteMessage, "disable", row.id)}
                                        >
                                            Stop Ad
                                        </Button>
                                    )}
                                    
                                </Box>
                            </TableCell>
                        }
                    </TableRow>
                )
            ) : adStatus === "draft" ? (
                pageData.map((row) =>
                    <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <CampaignEditCell ad={row} fetchData={fetchData} />
                        <TableCell align="right" width={350}>
                            <Box display="flex" flexDirection="row" justifyContent="flex-end" gap={2}>
                                <Button
                                    variant="contained"
                                    onClick={() => showAlert(submitMessage, "submit", row.id)}
                                >
                                    Submit for Review
                                </Button>
                                <Button
                                    variant={"outlined"}
                                    onClick={() => showAlert(deleteMessage, "disable", row.id)}
                                >
                                    Archive
                                </Button>
                            </Box>
                        </TableCell>
                    </TableRow>
                )
            ) : (
                pageData.map((row) =>
                    <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <CampaignEditCell
                            ad={row}
                            fetchData={fetchData}
                            showEditButton={row.adStatus === "rejected"}
                        />
                        <TableCell align="right">
                            {row.adStatus === "pending approval" ? (
                                <Typography>Waiting to be reviewed</Typography>
                            ) : (
                                <Typography
                                    color={statratRed}
                                    onClick={() => showAlert(row.adminMsg, "", 0)}
                                    sx={{ cursor: "pointer" }}
                                >
                                    Rejected - View Message
                                </Typography>
                            )}
                        </TableCell>
                        <TableCell align="right" width={350}>
                            {row.adStatus === "pending approval" ? (
                                <Box display="flex" justifyContent="flex-end">
                                    <Button
                                        variant="outlined"
                                        onClick={() => showAlert(cancelMessage, "cancel", row.id)}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            ) : (
                                <Box display="flex" flexDirection="row" justifyContent="flex-end" gap={2}>
                                    <Button
                                        variant="contained"
                                        onClick={() => showAlert(submitMessage, "submit", row.id)}
                                    >
                                        Resubmit for Review
                                    </Button>
                                    <Button
                                        variant={"outlined"}
                                        onClick={() => showAlert(deleteMessage, "disable", row.id)}
                                    >
                                        Archive
                                    </Button>
                                </Box>
                            )}
                        </TableCell>
                    </TableRow>
                )
            )}
        </TableBody>
    )
}

export default Rows;
